import clsx from 'clsx'
import parse from 'html-react-parser'
import { type ComponentPropsWithRef, type MouseEvent, type ReactElement } from 'react'

import { useCtaTracking } from '@lib/hooks/use-cta-tracking'

import { updateAssetCDNUrl } from '@components/asset/utils'
import { getPictureAttributes } from '@components/contentful-content-types/asset-image/utils'
import { Button } from '@components/ui-core/button/button'
import { Column } from '@components/ui-core/column/column'
import { Grid } from '@components/ui-core/grid/grid'
import { Picture } from '@components/ui-core/picture/picture'
import { Section } from '@components/ui-core/section/section'

import { HomepageText } from '@custom/pages/homepage/text/homepage-text'

import { type HomepageData } from '../types'

import styles from './manifesto-section.module.css'

export interface ManifestoSectionProps extends ComponentPropsWithRef<'section'> {
  data?: HomepageData['manifestoSection']
}

export function ManifestoSection(props: ManifestoSectionProps): ReactElement {
  const { className, children, data, ...rest } = props
  const { heading, copy, link, media } = data?.content || {}
  const { trackAndNavigate, isLoading } = useCtaTracking({ placement: 'Section' })

  const handleClick = (e: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLButtonElement>) => {
    if (!link?.fields?.targetUrl) {
      return
    }
    trackAndNavigate(
      'ReadAnnouncement',
      link?.fields?.title ?? '',
      'secondary',
      link?.fields?.targetUrl,
      e,
    )
  }

  const manifestoPictureAttributes = getPictureAttributes({
    width: media?.image?.width ?? 0,
    height: media?.image?.height ?? 0,
    url: updateAssetCDNUrl(media?.image?.path),
    isSvg: false,
    cols: 6,
  })

  return (
    <Section style={{ backgroundColor: 'var(--green-90)' }} data-color-scheme="dark" {...rest}>
      <Grid isReversed className={styles.grid}>
        <Column crossAxisAlignment="start" className={styles.column}>
          <HomepageText as="h2" variant="headingMedium" className={styles.heading}>
            {heading}
          </HomepageText>
          {copy && (
            <HomepageText as="p" variant="copy" className={styles.copy}>
              {parse(copy)}
            </HomepageText>
          )}
          <Button
            category="secondary"
            text={link?.fields?.title}
            variant="heart"
            href={link?.fields?.targetUrl}
            onClick={handleClick}
            isLoading={isLoading}
          />
        </Column>
        <Picture
          src={manifestoPictureAttributes.src}
          srcSets={manifestoPictureAttributes.srcSets}
          height={manifestoPictureAttributes.height}
          width={manifestoPictureAttributes.width}
          sizes={manifestoPictureAttributes.sizes}
          alt={media?.image?.alt || ''}
          className={clsx(styles.image, 'full-bleed')}
        />
      </Grid>
    </Section>
  )
}
